import { ETH_NULL_ADDRESS, XO_ID } from '../constants.js';
import { InvalidAmountError, InvalidFromTokenAddressError, InvalidToTokenAddressError, SimulationError, } from '../errors.js';
import BaseTransaction from './BaseTransaction.js';
import { whitelistedSimulationErrors } from '../providers/indirect/constants.js';
const ALLOWED_AMOUNTS_DIFF_PERCENTAGE = 10;
const SIMULATION_BASE_ASSET_SYMBOL_MAP = new Map([['MATICNATIVE', 'POL']]);
const areAmountsValid = (amount1, amount2) => {
    const difference = amount1.sub(amount2).abs();
    const percentOfLarger = amount1.gt(amount2)
        ? amount1.mul(ALLOWED_AMOUNTS_DIFF_PERCENTAGE).div(100)
        : amount2.mul(ALLOWED_AMOUNTS_DIFF_PERCENTAGE).div(100);
    return difference.lte(percentOfLarger);
};
class EvmTransaction extends BaseTransaction {
    data;
    to;
    value;
    from;
    gasPrice;
    tipGasPrice;
    gasLimit;
    chainId;
    constructor(params) {
        super({ action: params.action, type: 'evm', kind: params.kind ?? 'amm' });
        this.data = params.data;
        this.to = params.to;
        this.value = params.value;
        this.from = params.from;
        this.gasPrice = params.gasPrice;
        this.tipGasPrice = params.tipGasPrice;
        this.gasLimit = params.gasLimit;
        this.chainId = params.chainId;
    }
    async validate(params) {
        if (this.action !== 'swap') {
            return;
        }
        const fromTokenAddress = this.#getTokenAddress(params.fromAsset);
        const toTokenAddress = this.#getTokenAddress(params.toAsset);
        const previewEvmTxs = (params.requiredPreviewTxs ?? []).filter((tx) => tx.type === 'evm');
        const simulationResults = await params.simulateTx({
            baseAssetName: params.fromAsset.network,
            txs: [
                ...previewEvmTxs.map((tx) => ({
                    chainId: String(tx.chainId),
                    from: tx.from,
                    to: tx.to,
                    gas: tx.gasLimit.toString(),
                    gasPrice: '0x0',
                    value: tx.value || '0x0',
                    data: tx.data,
                    nonce: '0x0',
                    maxPriorityFeePerGas: undefined,
                    maxFeePerGas: undefined,
                })),
                {
                    chainId: String(this.chainId),
                    from: this.from,
                    to: this.to,
                    gas: this.gasLimit.toString(),
                    gasPrice: '0x0',
                    value: this.value || '0x0',
                    data: this.data,
                    nonce: '0x0',
                    maxPriorityFeePerGas: undefined,
                    maxFeePerGas: undefined,
                },
            ],
            senderAddress: params.fromAddress,
            origin: 'https://exchange.exodus.com',
        });
        const { balanceChanges: { willSend = [], willReceive = [] } = {}, metadata: { humanReadableError = undefined } = {}, } = simulationResults || {};
        if ((humanReadableError && !whitelistedSimulationErrors.has(humanReadableError)) ||
            willSend.length === 0) {
            throw new SimulationError({
                message: humanReadableError || 'Simulation failed',
            });
        }
        if ([params.fromAsset.id, params.toAsset.id].includes(XO_ID)) {
            return;
        }
        this.#validateTokenAddresses({
            willSend,
            willReceive,
            fromAsset: params.fromAsset,
            toAsset: params.toAsset,
            fromTokenAddress,
            toTokenAddress,
        });
        const totalSendingAmount = willSend.reduce((acc, { balance }) => {
            return acc.add(params.fromAsset.currency.defaultUnit(balance.toDefaultString()));
        }, params.fromAsset.currency.defaultUnit(0));
        if (params.swapMode === 'ExactIn') {
            if (!areAmountsValid(totalSendingAmount, params.fromAmount)) {
                throw new InvalidAmountError({
                    expected: params.fromAmount.toDefaultString(),
                    actual: totalSendingAmount.toDefaultString(),
                    order: null,
                    providerOrder: this.toJSON(),
                    willSend,
                    willReceive,
                });
            }
        }
        else {
            const maxAllowedAmount = params.fromAmount.mul(1 + params.slippage / 100);
            if (!areAmountsValid(totalSendingAmount, maxAllowedAmount)) {
                throw new InvalidAmountError({
                    expected: maxAllowedAmount.toDefaultString(),
                    actual: totalSendingAmount.toDefaultString(),
                    order: null,
                    providerOrder: this.toJSON(),
                    willSend,
                    willReceive,
                });
            }
        }
        if (params.isChained) {
            return;
        }
        if (willReceive.length === 0) {
            throw new InvalidAmountError({
                expected: params.toAmount.toDefaultString(),
                actual: '0',
                order: null,
                providerOrder: this.toJSON(),
                willSend,
                willReceive,
            });
        }
        const toAssetBalanceChanges = willReceive.filter((receive) => {
            if (!receive.asset)
                return false;
            if (toTokenAddress === ETH_NULL_ADDRESS || !receive.asset.address) {
                const mappedSymbol = this.#getSimulationBaseAssetSymbol(params.toAsset);
                return mappedSymbol === receive.asset.symbol;
            }
            return receive.asset.address.toLowerCase() === toTokenAddress.toLowerCase();
        });
        const receivingAmount = toAssetBalanceChanges.reduce((acc, { balance }) => {
            return acc.add(params.toAsset.currency.defaultUnit(balance.toDefaultString()));
        }, params.toAsset.currency.defaultUnit(0));
        if (!areAmountsValid(receivingAmount, params.toAmount)) {
            throw new InvalidAmountError({
                expected: params.toAmount.toDefaultString(),
                actual: receivingAmount.toDefaultString(),
                order: null,
                providerOrder: this.toJSON(),
                willSend,
                willReceive,
            });
        }
    }
    #validateTokenAddresses(params) {
        const { willSend, willReceive, fromAsset, toAsset, fromTokenAddress, toTokenAddress } = params;
        const isValidFromToken = willSend.every((send) => {
            if (!send.asset)
                return false;
            if (fromTokenAddress === ETH_NULL_ADDRESS) {
                const mappedSymbol = this.#getSimulationBaseAssetSymbol(send.asset);
                return mappedSymbol === send.asset.symbol;
            }
            return send.asset.address?.toLowerCase() === fromTokenAddress?.toLowerCase();
        });
        if (!isValidFromToken) {
            throw new InvalidFromTokenAddressError({
                actual: willSend.map((send) => send.asset?.address || send.asset?.symbol).join(', '),
                expected: fromTokenAddress || fromAsset.symbol,
                order: null,
                providerOrder: this.toJSON(),
            });
        }
        const isValidToToken = willReceive.every((receive) => {
            if (!receive.asset)
                return false;
            if (toTokenAddress === ETH_NULL_ADDRESS) {
                const mappedSymbol = this.#getSimulationBaseAssetSymbol(receive.asset);
                return mappedSymbol === receive.asset.symbol;
            }
            return receive.asset.address?.toLowerCase() === toTokenAddress?.toLowerCase();
        });
        if (!isValidToToken) {
            throw new InvalidToTokenAddressError({
                actual: willReceive
                    .map((receive) => receive.asset?.address || receive.asset?.symbol)
                    .join(', '),
                expected: toTokenAddress || toAsset.symbol,
                order: null,
                providerOrder: this.toJSON(),
            });
        }
    }
    async execute(params) {
        const tx = await params.createAndSendEvmTx({
            contractAddress: this.to,
            assetId: params.fromAsset.id,
            fromAddress: this.from,
            gasLimit: this.gasLimit,
            gasPrice: this.gasPrice,
            tipGasPrice: this.tipGasPrice,
            txInput: this.data,
            amount: params.amount,
            txValue: this.value,
        });
        return tx.txId;
    }
    async allowance(params) {
        const tx = await params.createAndSendEvmTx({
            assetId: params.fromAsset.id,
            fromAddress: this.from,
            gasLimit: this.gasLimit,
            gasPrice: this.gasPrice,
            tipGasPrice: this.tipGasPrice,
            txInput: this.data,
            amount: params.fromAsset.currency.defaultUnit(0),
            contractAddress: this.to,
        });
        return tx.txId;
    }
    toJSON() {
        return {
            action: this.action,
            type: this.type,
            data: this.data,
            to: this.to,
            value: this.value,
            from: this.from,
            gasPrice: this.gasPrice,
            tipGasPrice: this.tipGasPrice,
            gasLimit: this.gasLimit,
            chainId: this.chainId,
            kind: this.kind,
        };
    }
    #getTokenAddress = (asset) => {
        return asset.meta?.contractAddress?.toLowerCase() || ETH_NULL_ADDRESS;
    };
    #getSimulationBaseAssetSymbol(asset) {
        return SIMULATION_BASE_ASSET_SYMBOL_MAP.get(asset.id) ?? asset.symbol;
    }
}
export default EvmTransaction;
